import type { GemxQlQueryVariables } from '~/core/gemxql/graphql';
import type { EAnalyticMode } from '~/modules/analytics/types';
import type { GemXQlVersionIds } from '~/core/gemxql/types';
import type { AnalyticDateFilter } from '~/presentation/ui';

import {
  EAnalyticSource,
  EComparisonOperator,
  EFilterField,
  EGroupWithClause,
  EOrderDirectionType,
  EPageDimension,
  EPageField,
} from '~/core/gemxql/types';
import { CAMPAIGN_PATHS_EXPERIMENT_VARIANT_ID_FIELD } from '../constants';
import { buildGemXQlQuery } from '~/core/gemxql/builder';
import { EMetricKey } from '~/types/metric';

export interface IBuildCampaignPathsQueryVariablesParams {
  mode: EAnalyticMode;
  campaignVariantID: string;
  campaignVersionID?: string;
  dateRange: AnalyticDateFilter;
}

/**
 * Builds the GemxQL query for campaign first-level paths, replacing the legacy `campaignPaths`
 * GraphQL query. The mode drives which sessions are counted (FIRST_SESSION = entry pages only,
 * ALL_SESSION = all pages in the journey).
 */
export const buildCampaignPathsQueryVariables = ({
  mode,
  campaignVariantID,
  campaignVersionID,
  dateRange,
}: IBuildCampaignPathsQueryVariablesParams): GemxQlQueryVariables => {
  // `version_id` scopes to a specific campaign version; applied in both modes when present. This is
  // the version filter field, NOT `version` (which is the A/B variant type).
  const versionIds: GemXQlVersionIds | undefined = campaignVersionID
    ? { [EFilterField.CAMPAIGN_VERSION_ID]: campaignVersionID }
    : undefined;

  const input = buildGemXQlQuery({
    mode,
    sources: [EAnalyticSource.SESSIONS],
    metrics: [EMetricKey.SESSION, EPageField.PAGE_TYPE, EPageField.PAGE_TITLE],
    dimensions: [EPageDimension.PAGE_PATH],
    groupWithClause: EGroupWithClause.NONE,
    dateRange,
    filters: {},
    versionIds,
    extraConditions: [
      {
        operator: EComparisonOperator.EQ,
        fields: [CAMPAIGN_PATHS_EXPERIMENT_VARIANT_ID_FIELD],
        value: campaignVariantID,
      },
    ],
    // Order by session count, then path — matches the legacy result ordering.
    orderBy: {
      [EMetricKey.SESSION]: { field: EMetricKey.SESSION, direction: EOrderDirectionType.DESC },
      [EPageDimension.PAGE_PATH]: {
        field: EPageDimension.PAGE_PATH,
        direction: EOrderDirectionType.ASC,
      },
    },
  });

  return { input };
};
